
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { PerformanceGoal, performanceService } from "./services/performanceService";
import { toast } from "sonner";

interface EditGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: PerformanceGoal | null;
  onGoalUpdated: () => void;
}

export const EditGoalDialog = ({ open, onOpenChange, goal, onGoalUpdated }: EditGoalDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    goal_title: goal?.goal_title || '',
    goal_description: goal?.goal_description || '',
    goal_category: goal?.goal_category || 'financial',
    goal_type: goal?.goal_type || 'performance',
    target_value: goal?.target_value || '',
    current_progress: goal?.current_progress || 0,
    status: goal?.status || 'active',
    priority: goal?.priority || 'medium',
    start_date: goal?.start_date ? new Date(goal.start_date).toISOString().split('T')[0] : '',
    target_date: goal?.target_date ? new Date(goal.target_date).toISOString().split('T')[0] : ''
  });

  // Helper function to format date for display (DD MMM YYYY)
  const formatDateDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Update form data when goal changes
  useEffect(() => {
    if (goal) {
      setFormData({
        goal_title: goal.goal_title,
        goal_description: goal.goal_description || '',
        goal_category: goal.goal_category,
        goal_type: goal.goal_type,
        target_value: goal.target_value || '',
        current_progress: goal.current_progress,
        status: goal.status,
        priority: goal.priority,
        start_date: goal.start_date ? new Date(goal.start_date).toISOString().split('T')[0] : '',
        target_date: goal.target_date ? new Date(goal.target_date).toISOString().split('T')[0] : ''
      });
    }
  }, [goal]);

  const handleTargetValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, target_value: value });
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow empty string for clearing the field
    if (value === '') {
      setFormData({ ...formData, current_progress: 0 });
      return;
    }

    // Only allow numbers and decimal points
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    const numericValue = parseFloat(value);
    
    // Don't allow negative values
    if (numericValue < 0) {
      return;
    }

    // Don't allow values greater than 1000
    if (numericValue > 1000) {
      return;
    }

    setFormData({ ...formData, current_progress: numericValue });
  };

  const handleTargetValueBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Remove validation - allow any text string
  };

  const handleProgressBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    
    const numericValue = parseFloat(value);
    if (numericValue < 0) {
      setFormData({ ...formData, current_progress: 0 });
      toast.error("Progress cannot be negative");
    } else if (numericValue > 1000) {
      setFormData({ ...formData, current_progress: 1000 });
      toast.error("Progress cannot exceed 1000");
    }
  };

  const handleSubmit = async () => {
    if (!goal) return;

    if (!formData.goal_title || !formData.goal_category || !formData.goal_type || !formData.status || !formData.priority || !formData.start_date || !formData.target_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        goal_title: formData.goal_title,
        goal_description: formData.goal_description,
        goal_category: formData.goal_category,
        goal_type: formData.goal_type,
        target_value: formData.target_value,
        current_progress: formData.current_progress,
        status: formData.status,
        priority: formData.priority,
        start_date: formData.start_date,
        target_date: formData.target_date
      };

      await performanceService.updatePerformanceGoal(goal.id, updateData);
      toast.success("Goal updated successfully!");
      onGoalUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating goal:", error);
      toast.error("Failed to update goal");
    } finally {
      setIsLoading(false);
    }
  };

  const isGoalFrozen = goal?.is_frozen || false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white border-gray-400">
        <DialogHeader>
          <DialogTitle className="text-black">Edit Goal</DialogTitle>
          {isGoalFrozen && (
            <div className="text-orange-600 text-sm font-medium mt-2">
              Goals are frozen. You can still update the progress and status for the goal
            </div>
          )}
          <DialogDescription className="text-gray-600">
            Update the goal details below.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Row 1: Goal Title and Goal Type - labels and inputs on same horizontal level */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="goal_title" className="text-black whitespace-nowrap min-w-[90px]">Goal Title *</Label>
              <Input
                id="goal_title"
                value={formData.goal_title}
                onChange={(e) => setFormData({ ...formData, goal_title: e.target.value })}
                className={cn(
                  "flex-1",
                  isGoalFrozen 
                    ? "bg-gray-100 border-gray-300 text-black cursor-not-allowed" 
                    : "bg-white border-gray-400 text-black"
                )}
                disabled={isGoalFrozen}
              />
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="goal_type" className="text-black whitespace-nowrap min-w-[90px]">Goal Type *</Label>
              <Select 
                value={formData.goal_type} 
                onValueChange={(value) => setFormData({ ...formData, goal_type: value as "performance" | "behavioral" })}
                disabled={isGoalFrozen}
              >
                <SelectTrigger className={cn(
                  "flex-1",
                  isGoalFrozen 
                    ? "bg-gray-100 border-gray-300 text-black cursor-not-allowed" 
                    : "bg-white border-gray-400 text-black"
                )}>
                  <SelectValue placeholder="Select goal type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="behavioral">Behavioral</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Goal Description - Label and textarea on same horizontal row */}
          <div className="flex items-start gap-3">
            <Label htmlFor="goal_description" className="text-black whitespace-nowrap min-w-[90px] mt-2">Description</Label>
            <Textarea
              id="goal_description"
              value={formData.goal_description}
              onChange={(e) => setFormData({ ...formData, goal_description: e.target.value })}
              className={cn(
                "flex-1",
                isGoalFrozen 
                  ? "bg-gray-100 border-gray-300 text-black cursor-not-allowed" 
                  : "bg-white border-gray-400 text-black"
              )}
              rows={3}
              disabled={isGoalFrozen}
            />
          </div>

          {/* Row 2: Goal Category and Target - labels and inputs on same horizontal level */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="goal_category" className="text-black whitespace-nowrap min-w-[90px]">Category *</Label>
              <Select 
                value={formData.goal_category} 
                onValueChange={(value) => setFormData({ ...formData, goal_category: value as "financial" | "project_delivery" | "customer_impact" | "innovation" | "leadership_behaviors" })}
                disabled={isGoalFrozen}
              >
                <SelectTrigger className={cn(
                  "flex-1",
                  isGoalFrozen 
                    ? "bg-gray-100 border-gray-300 text-black cursor-not-allowed" 
                    : "bg-white border-gray-400 text-black"
                )}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="project_delivery">Project Delivery</SelectItem>
                  <SelectItem value="customer_impact">Customer Impact</SelectItem>
                  <SelectItem value="innovation">Innovation</SelectItem>
                  <SelectItem value="leadership_behaviors">Leadership Behaviours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="target_value" className="text-black whitespace-nowrap min-w-[90px]">Target</Label>
              <Input
                id="target_value"
                value={formData.target_value}
                onChange={handleTargetValueChange}
                onBlur={handleTargetValueBlur}
                className={cn(
                  "flex-1",
                  isGoalFrozen 
                    ? "bg-gray-100 border-gray-300 text-black cursor-not-allowed" 
                    : "bg-white border-gray-400 text-black"
                )}
                placeholder="Enter target value"
                disabled={isGoalFrozen}
              />
            </div>
          </div>

          {/* Row 3: Progress and Priority - labels and inputs on same horizontal level */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="current_progress" className="text-black whitespace-nowrap min-w-[90px]">Progress *</Label>
              <Input
                id="current_progress"
                type="text"
                value={formData.current_progress}
                onChange={handleProgressChange}
                onBlur={handleProgressBlur}
                className="bg-white border-gray-400 text-black flex-1"
                placeholder="0-1000"
              />
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="priority" className="text-black whitespace-nowrap min-w-[90px]">Priority *</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value) => setFormData({ ...formData, priority: value as "high" | "medium" | "low" })}
                disabled={isGoalFrozen}
              >
                <SelectTrigger className={cn(
                  "flex-1",
                  isGoalFrozen 
                    ? "bg-gray-100 border-gray-300 text-black cursor-not-allowed" 
                    : "bg-white border-gray-400 text-black"
                )}>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 4: Status and blank field - labels and inputs on same horizontal level */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="status" className="text-black whitespace-nowrap min-w-[90px]">Status *</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData({ ...formData, status: value as "active" | "paused" | "completed" | "cancelled" })}
              >
                <SelectTrigger className="bg-white border-gray-400 text-black flex-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              {/* Empty space to maintain grid alignment */}
            </div>
          </div>

          {/* Row 5: Start Date and Target Date - labels and date inputs on same horizontal level */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="start_date" className="text-black whitespace-nowrap min-w-[90px]">Start Date *</Label>
              <div className="flex-1 space-y-1">
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className={cn(
                    "bg-white border-gray-400 text-black",
                    isGoalFrozen && "bg-gray-100 border-gray-300 cursor-not-allowed"
                  )}
                  disabled={isGoalFrozen}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="target_date" className="text-black whitespace-nowrap min-w-[90px]">Target Date *</Label>
              <div className="flex-1 space-y-1">
                <Input
                  id="target_date"
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                  className={cn(
                    "bg-white border-gray-400 text-black",
                    isGoalFrozen && "bg-gray-100 border-gray-300 cursor-not-allowed"
                  )}
                  disabled={isGoalFrozen}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-gray-400 text-black hover:bg-primary/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90 text-black"
          >
            {isLoading ? "Updating..." : "Update Goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
